import React, { useState } from 'react';
import { useSource } from './useSource';
import { useConversation } from './useConversation';
import { BASE_URL, IMessage } from './types';
import { Events, logEvent } from '../../utils/logging/events';
import { MessageView } from './MessageView';
import { DisclaimerSheet, alreadyAccepted } from './DisclaimerSheet';
import {
  ArrowUpIcon,
  HomeIcon,
  PlusIcon,
  ThumbDownIcon,
  ThumbUpIcon,
} from '../../utils/icons';

interface IChatboxProps {
  disabled?: boolean;
}

const SUGGESTIONS = [
  'Does swallowing water while brushing break my fast?',
  'How do I pray Qasr when travelling?',
  'What invalidates wudu?',
];

const Chatbox: React.FC<IChatboxProps> = ({ disabled }) => {
  const { source } = useSource();
  const { conversationId, updateConversationId } = useConversation(source);

  const [messages, setMessages] = useState<IMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  // Conversation whose history has been fetched. Sending before that would let
  // the late fetch overwrite the optimistic user message.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(alreadyAccepted);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<'good' | 'bad' | null>(null);
  const scroller = React.useRef<HTMLDivElement>(null);
  const input = React.useRef<HTMLTextAreaElement>(null);

  const newChat = React.useCallback(() => {
    sessionStorage.removeItem('conversationId' + source);
    setMessages([]);
    setError(null);
    setPending(false);
    setFeedback(null);
    updateConversationId();
  }, [source, updateConversationId]);

  const loadMessages = React.useCallback(() => {
    if (conversationId === null) return;
    return fetch(`${BASE_URL}/messages?conversation_id=${conversationId}`)
      .then((res) => {
        if (res.status !== 200) return newChat();
        return res.json().then((data) => setMessages(data as IMessage[]));
      })
      .catch(() => newChat());
  }, [conversationId, newChat]);

  // A question linked from the home page always starts a fresh conversation,
  // so it isn't appended to (or hidden behind) a previous one.
  const checkedLink = React.useRef(false);
  React.useEffect(() => {
    if (!checkedLink.current) {
      checkedLink.current = true;
      if (new URLSearchParams(window.location.search).get('q')) {
        sessionStorage.removeItem('conversationId' + source);
      }
    }
    updateConversationId();
  }, [updateConversationId, source]);

  React.useEffect(() => {
    const id = conversationId;
    loadMessages()?.then(() => id && setLoadedFor(id));
  }, [loadMessages, conversationId]);

  React.useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, pending, error]);

  React.useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 180)}px`;
  }, [draft]);

  const send = React.useCallback(
    (override?: string) => {
      const text = (override ?? draft).trim();
      if (
        !accepted ||
        !text ||
        pending ||
        disabled ||
        conversationId === null ||
        loadedFor !== conversationId
      )
        return;
      setError(null);
      setPending(true);
      setDraft('');
      setMessages((prev) => [...prev, { content: text, role: 'user' }]);
      logEvent(Events.SEND_MESSAGE);
      fetch(`${BASE_URL}/chatbot`, {
        method: 'POST',
        body: JSON.stringify({
          marjaa: source,
          conversation_id: conversationId,
          message: text,
        }),
        headers: { 'Content-Type': 'application/json' },
      })
        .then(async (res) => {
          if (res.status === 200) {
            await loadMessages();
            return;
          }
          setMessages((prev) => prev.slice(0, -1));
          setDraft(text);
          const body = [409, 413].includes(res.status)
            ? await res.text()
            : null;
          setError(
            body ||
              (res.status === 429
                ? 'Too many messages. Wait a minute, then send again. There is also a daily limit.'
                : 'Something went wrong on our side. Your question is still in the box, so try sending it again.'),
          );
        })
        .catch(() => {
          setMessages((prev) => prev.slice(0, -1));
          setDraft(text);
          setError(
            'Could not reach the server. Check your connection and send again.',
          );
        })
        .finally(() => setPending(false));
    },
    [
      accepted,
      draft,
      pending,
      disabled,
      conversationId,
      loadedFor,
      source,
      loadMessages,
    ],
  );

  // Links like /ask?q=... (from the home page) send their question once.
  const sentLinkedQuestion = React.useRef(false);
  React.useEffect(() => {
    if (
      sentLinkedQuestion.current ||
      !accepted ||
      conversationId === null ||
      loadedFor !== conversationId ||
      disabled
    )
      return;
    const q = new URLSearchParams(window.location.search).get('q');
    if (!q) return;
    sentLinkedQuestion.current = true;
    window.history.replaceState(null, '', window.location.pathname);
    send(q.slice(0, 1000));
  }, [accepted, conversationId, loadedFor, disabled, send]);

  const sendFeedback = (value: 'good' | 'bad') => {
    if (conversationId === null || feedback) return;
    setFeedback(value);
    fetch(`${BASE_URL}/feedback`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversationId,
        feedback: value,
      }),
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  // The server opens every conversation with a greeting; the empty state replaces it.
  const started = messages.some((m) => m.role === 'user');
  const visible = started ? messages : [];
  const lastIsAnswer =
    started && !pending && messages[messages.length - 1]?.role === 'assistant';

  return (
    <div className="relative flex h-[100svh] flex-col text-left">
      <DisclaimerSheet accepted={accepted} onAccept={() => setAccepted(true)} />
      <div className="lattice pointer-events-none absolute inset-x-0 top-0 h-80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      <header className="relative z-10 border-b border-line bg-ground/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <a
            href="/"
            aria-label="Home"
            onClick={() => logEvent(Events.HOME_BUTTON)}
            className="rounded-full p-2 text-muted hover:bg-line hover:text-ink"
          >
            <HomeIcon />
          </a>
          <div>
            <div className="font-display text-title">Ayatollah {source}</div>
            <div className="text-caption text-muted">
              Answers from published rulings
            </div>
          </div>
          <button
            type="button"
            onClick={newChat}
            className="ml-auto inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-ui hover:border-peach hover:text-peach transition-colors"
          >
            <PlusIcon /> New chat
          </button>
        </div>
      </header>

      <div ref={scroller} className="relative flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full max-w-3xl flex-col gap-8 px-4 py-8">
          {disabled && (
            <div className="rounded-container border border-peach bg-surface px-5 py-4 text-ui">
              Salam! The assistant is under maintenance while we add new books
              and Q&As. Please check back soon.
            </div>
          )}

          {!started && (
            <div className="my-auto py-10">
              <h1 className="rise balance font-display text-display-lg-sm md:text-display-lg">
                What would you like
                <br />
                to ask?
              </h1>
              <p
                className="rise mt-4 max-w-md text-body text-muted"
                style={{ animationDelay: '0.1s' }}
              >
                Ask in your own words. If something is unclear, I'll ask you
                back before looking it up.
              </p>
              <ul
                className="rise mt-10 !pl-0 divide-y divide-line border-y border-line"
                style={{ animationDelay: '0.2s' }}
              >
                {SUGGESTIONS.map((s) => (
                  <li key={s} className="!list-none">
                    <button
                      type="button"
                      disabled={
                        loadedFor !== conversationId ||
                        conversationId === null ||
                        disabled
                      }
                      onClick={() => send(s)}
                      className="flex w-full items-center justify-between gap-4 py-4 text-left text-body hover:text-peach disabled:opacity-50 transition-colors"
                    >
                      {s}
                      <span className="text-muted">
                        <ArrowUpIcon />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {visible.map((m, i) => (
            <MessageView
              key={i}
              message={m}
              question={
                visible[i - 1]?.role === 'user'
                  ? visible[i - 1].content
                  : undefined
              }
            />
          ))}

          {pending && (
            <div
              className="border-l-2 border-peach-line pl-5 text-muted"
              role="status"
            >
              <span className="text-body">Searching the rulings</span>
              <span className="bouncing-loader !justify-start !inline-flex ml-2 align-middle">
                <div />
                <div />
                <div />
              </span>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-container border border-danger bg-surface px-5 py-4 text-ui"
            >
              {error}
            </div>
          )}

          {lastIsAnswer && (
            <div className="flex items-center gap-2 pl-5 text-ui text-muted">
              {feedback === null
                ? 'Was this helpful?'
                : 'Thanks for the feedback.'}
              {(['good', 'bad'] as const).map(
                (v) =>
                  (feedback === null || feedback === v) && (
                    <button
                      key={v}
                      type="button"
                      onClick={() => sendFeedback(v)}
                      aria-label={v === 'good' ? 'Helpful' : 'Not helpful'}
                      aria-pressed={feedback === v}
                      className={`rounded-full p-2 transition-colors ${
                        feedback === v
                          ? v === 'good'
                            ? 'bg-success text-navy'
                            : 'bg-danger text-navy'
                          : 'hover:bg-line hover:text-ink'
                      }`}
                    >
                      {v === 'good' ? <ThumbUpIcon /> : <ThumbDownIcon />}
                    </button>
                  ),
              )}
            </div>
          )}
        </div>
      </div>

      <div className="relative z-10 bg-gradient-to-t from-ground from-70% to-transparent pt-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="mx-auto max-w-3xl px-4"
        >
          <div className="flex items-end gap-3 rounded-container border border-line bg-surface py-2 pl-5 pr-2 transition-colors focus-within:border-peach">
            <textarea
              ref={input}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={disabled}
              aria-label="Your question"
              placeholder="Type your question"
              className="max-h-44 min-h-10 flex-1 resize-none self-center bg-transparent py-2 text-body outline-none placeholder:text-muted-soft disabled:opacity-50"
            />
            <button
              type="submit"
              aria-label="Send"
              disabled={
                !draft.trim() || pending || disabled || conversationId === null
              }
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-peach text-navy transition-opacity hover:bg-peach-soft disabled:bg-line disabled:text-muted-soft"
            >
              <ArrowUpIcon />
            </button>
          </div>
          <p className="py-3 text-center text-caption text-muted-soft">
            Answers are AI-generated and can contain errors. Verify with the
            sources or a scholar.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Chatbox;
